import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch, Ref, Prop } from 'vue-property-decorator';
import {HomeworksService} from '@/homework/services/homeworks.service';
import {HOMEWORK_HISTORY_STATUS, HOMEWORKS_EVENTS} from '@/homework/values/homeworks.values';
import Moment from 'moment';
import {SCREEN_MIRRORING_EVENTS} from '@/values/screen.mirroring.values';
import {TOAST_TYPES} from '@/plugins/ed-toast/values/ed-toast.values';
import KEYS from '@/values/keys';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import ToolService from '@/providers/tools';
import { WboPersistenceLocatorProvider } from '@/components/common/ed-board/ed-board-tools/wbo-client/persistence/wbo-persistence-locator-provider';

@Component({
  name: 'ed-list-homework-history'
})
export default class EdListHomeworkHistoryComponent extends Vue {

  public ROWS_X_PAGE = 10;
  public loading: boolean = true;
  public versionId: string;

  /* Props */
  @Prop() readonly params: any;

  public pagination: any = { current: 1, total: 0 }
  public items: Array<Object> = [];

  public itemSelected: Object = {};
  public allowRestore: boolean = true;
  @Ref('dataTable') readonly dataTable: any;

  /**
   * Hooks
   */
  mounted() {
    this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
    this.checkPendingChanges().then(() =>  this.loadItems());
  }

  beforeDestroy() {
    this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }

  get info(){
    return this.params.data;
  }

  get sectionName(): string {
    return ToolService.exitSpecialChars(this.params.data.sectionName);
  }

  /**
   * Get the homwework hash and number of pages to check if there are local pending changes
   */
  async checkPendingChanges() {
    let numberPages = 0;
    let boardGroupHash = '';
    let data : any;    
    if(this.info.historyStatus === HOMEWORK_HISTORY_STATUS.RESPONSE){
      data = await HomeworksService.obtainStudentWhiteboardHomeworkHash({classId: this.info.classId, sectionId: this.info.sectionId});
    } else  if(this.params.data.historyStatus === HOMEWORK_HISTORY_STATUS.FEEDBACK){
      data = await  HomeworksService.obtainWhiteboardHomeworkFeedbackHash(this.info.homeworkId);
    }
    numberPages = data?.numberPages || 0;
    boardGroupHash = data?.boardGroupHash;

    for (let i = 0; i < numberPages; i++) {
      const wboPersistenceProvider = WboPersistenceLocatorProvider.getInstance(`${boardGroupHash}_${i + 1}`);
      let readPendingChangesFile = await wboPersistenceProvider.readPendingChangesFile();
      if(readPendingChangesFile?.queue()?.size() > 0) this.allowRestore = false;
    }
  }

  /* Data */
  loadItems(){
    this.loading = true;
    try {
      HomeworksService.getHomeworkHistory(this.info.classId, this.info.sectionId,
        this.info.studentId, this.info.historyStatus).then((res)=>{
          // @ts-ignore
          this.items = res;
          if (this.items.length < this.ROWS_X_PAGE){
            this.pagination['total'] = 1;
          }else{
            this.pagination['total'] = Math.ceil(this.items.length / this.ROWS_X_PAGE);
          }
          this.loading = false;
      });
      return
    } catch(e) {
      this.loading = false;
    }
  }

  get headers() {
    return [
      { text: this.$t('homeworks.history.version'), value: 'id',  sortable: false },
      { text: this.$t('homeworks.history.date'), value: 'date', sortable: false },
      { text: this.$t('homeworks.history.source'), value: 'source',  sortable: false },
      { text: this.$t('homeworks.history.actions'), value: 'actions' }
    ]
  }

  get itemsValue() {
    return this.items;
  }

  openSnapShot(item){
    let data = this.info;
    data['info']=item;
    data['allowRestore'] = this.allowRestore;
    this.$edEventBus.$emit(HOMEWORKS_EVENTS.OPEN_HOMEWORK_SNAPSHOT, data);
    return true;
  }

  async recover(item){
    if(this.allowRestore){
      this.versionId = item.id;
      this.$EdModal.createConfirmationModal(
          {
            title: this.$i18n.t('homeworks.actionRestore'),
            body: `${this.$t('homeworks.questionActionRestore')}`,
            action: () => { this.doRecover(item); }
          }
      );
    }
  }

  doRecover(item){
    this.loading = true;
    this.itemSelected = item;
    let _this = this;
    this.dataTable?.updateItem(item);
    HomeworksService.recoverHistoryBoard(this.versionId).then(()=>{
      _this.closeModal();
      _this.$EdToast.create({
        msg: `${this.$t('homeworks.documentRestored', {versionId: this.versionId})}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      _this.itemSelected = {};
      _this.loading = false;
    }).catch(()=>{
      _this.$EdModal.createMessageModal({
        title: this.$i18n.t('homeworks.errorTitle'),
        body: this.$i18n.t('homeworks.errorDocumentRestored'),
        type: KEYS.EVENT_TYPES.DANGER
      });
      _this.loading = false;
    })
    .finally(() => {
      _this.dataTable?.updateItem(item);
    })
  }

  formatDate(date){
    return Moment(date).format('DD/MM/YYYY hh:mm A');
  }

  closeModal() {
    this.$EdModal.closeModal();
  }

  public onPageUpdate(customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;
    this.pagination['current'] = detail.page;
    this._loadHistoryInDataTable();
  }

  public isRecoveringItem(item: any): boolean {
    return this.loading && item['id'] === this.itemSelected['id'];
  }

  @Watch('itemsValue', { deep: true, immediate: true })
  private _watchItemsValue(): void {
    if (this.dataTable) {
      this._loadHistoryInDataTable();
    } else {
      this.$nextTick(() => {
        setTimeout(() => {
          this._loadHistoryInDataTable();
        }, 500);
      });
    }
  }

  private _onLanguageChanged(lang: string): void {
    this.$nextTick(() => {
      this.dataTable?.setAttribute('lang', lang);
      this._loadHistoryInDataTable();
    });
  }

  private _getItemsPerPage(page: number): any[] {
    const start = (page - 1) * this.ROWS_X_PAGE;
    const end = start + this.ROWS_X_PAGE;
    return this.items.slice(start, end);
  }

  private _loadHistoryInDataTable(): void {

    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.items || this.items.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }

    const slots = {
      'item.date': {
      template: '{item.date}',
      templateParams: [{
        key: 'item.date',
        value: (params) => params.parent.formatDate(params.item.date),
      }],
      },
      'item.source': {
      template: '{item.source}',
      templateParams: [{
        key: 'item.source',
        value: (params) => params.parent.$t('homeworks.sourceType.' + params.item.source.toLowerCase().replace(/\s+/g, '').replace(/-/g, '_')),
      }],
      },
      'item.actions': {
      template: document.querySelector('#item\\.actions').innerHTML,
      templateParams: [{}],
      expressions: [
        {
        target: '.spinner', attribute: 'style', booleanAttribute: false,
        expression: (params) => params.parent.isRecoveringItem(params.item),
        applied: 'display: inline;', fallback: 'display: none;'
        },
        {
        target: '.actionButtons', attribute: 'style', booleanAttribute: false,
        expression: (params) => !params.parent.isRecoveringItem(params.item),
        applied: 'display: inline;', fallback: 'display: none;'
        },
      ],
      events: [
        { target: '.previewVersion', handler: (params) => params.parent.openSnapShot(params.item) },
        { target: '.restoreVersion', handler: (params) => params.parent.recover(params.item) },
      ],
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this._getItemsPerPage(this.pagination.current),
      headers: this.headers,
      slots: slots,
    });
  }
}
